import React, { useState } from "react";
import { createPortal } from "react-dom";
import ProgressBar from "../common/ProgressBar.jsx";
import Sparkline from "../common/Sparkline.jsx";
import UploadDetailsModal from "./UploadDetailsModal.jsx";
import { useConfirm } from "../../context/ConfirmContext";
import { useUploadManager } from "../../context/UploadManagerContext";
import { fmtSpeed, fmtRate, fmtEta } from "../../utils/uploadStats";
import "../../css/UploadTray.css";

const isActive = (phase) => phase === "uploading" || phase === "finalizing";

const KIND_ICON = { import: "📦", quick: "📤" };

function cardStatus(s) {
    switch (s.phase) {
        case "uploading":
            return { text: "Uploading", tone: "accent" };
        case "finalizing":
            return { text: "Processing", tone: "accent" };
        case "done":
            return s.result.failed > 0 ? { text: `${s.result.failed} failed`, tone: "warning" } : { text: "Done", tone: "success" };
        case "error":
            return { text: "Failed", tone: "danger" };
        case "canceled":
            return { text: "Cancelled", tone: "muted" };
        default:
            return { text: "Pending", tone: "muted" };
    }
}

const UploadTray = () => {
    const confirm = useConfirm();
    const { sessions, presentedIds, trayOpen, setTrayOpen, cancelSession, dismissSession, clearFinished } =
        useUploadManager();
    const [detailsId, setDetailsId] = useState(null);

    const visible = sessions.filter((s) => !presentedIds.has(s.id));
    if (visible.length === 0) {
        // Nothing to show in the tray, but a details modal may still be open for a
        // "presented" session (its card is intentionally hidden behind the modal).
        return detailsId ? createPortal(<UploadDetailsModal sessionId={detailsId} onClose={() => setDetailsId(null)} />, document.body) : null;
    }

    const activeCount = visible.filter((s) => isActive(s.phase)).length;
    const doneCount = visible.length - activeCount;
    const summary =
        activeCount > 0
            ? `${activeCount} upload${activeCount === 1 ? "" : "s"} in progress`
            : `${visible.length} upload${visible.length === 1 ? "" : "s"} finished`;

    const handleX = async (e, s) => {
        e.stopPropagation();
        if (isActive(s.phase)) {
            const ok = await confirm({
                title: "Stop this upload?",
                message: "Files still uploading won't be finished.",
                confirmText: "Stop upload",
                danger: true,
            });
            if (ok) cancelSession(s.id);
        } else {
            dismissSession(s.id);
        }
    };

    return (
        <>
            <div className="uptray" role="region" aria-label="Uploads">
                <div className="uptray-header" onClick={() => setTrayOpen(!trayOpen)}>
                    <span className="uptray-header-title">
                        {activeCount > 0 && <span className="uptray-spin" aria-hidden="true" />}
                        Uploads
                    </span>
                    <span className="uptray-header-sub">{summary}</span>
                    <div className="uptray-header-actions">
                        {doneCount > 0 && (
                            <button
                                type="button"
                                className="uptray-iconbtn"
                                title="Clear finished"
                                aria-label="Clear finished uploads"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    clearFinished();
                                }}
                            >
                                ✕
                            </button>
                        )}
                        <button
                            type="button"
                            className="uptray-iconbtn"
                            title={trayOpen ? "Collapse" : "Expand"}
                            aria-label={trayOpen ? "Collapse uploads" : "Expand uploads"}
                            aria-expanded={trayOpen}
                            onClick={(e) => {
                                e.stopPropagation();
                                setTrayOpen(!trayOpen);
                            }}
                        >
                            {trayOpen ? "▾" : "▴"}
                        </button>
                    </div>
                </div>

                {trayOpen && (
                    <div className="uptray-list">
                        {visible.map((s) => {
                            const st = cardStatus(s);
                            const pct = Math.round((s.progress || 0) * 100);
                            const active = isActive(s.phase);
                            const speedText = s.rateUnit === "files" ? fmtRate(s.speed, "files") : fmtSpeed(s.speed);
                            return (
                                <div
                                    key={s.id}
                                    className="uptray-card"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setDetailsId(s.id)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            setDetailsId(s.id);
                                        }
                                    }}
                                    title="Open details"
                                >
                                    <div className="uptray-card-top">
                                        <span className="uptray-card-icon" aria-hidden="true">
                                            {KIND_ICON[s.kind] || "📤"}
                                        </span>
                                        <span className="uptray-card-title" title={s.title}>
                                            {s.title}
                                        </span>
                                        <span className="uptray-card-pct">{pct}%</span>
                                        <button
                                            type="button"
                                            className="uptray-iconbtn uptray-card-x"
                                            title={active ? "Stop upload" : "Dismiss"}
                                            aria-label={active ? "Stop upload" : "Dismiss"}
                                            onClick={(e) => handleX(e, s)}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    <ProgressBar
                                        value={pct}
                                        max={100}
                                        variant={st.tone === "muted" ? "accent" : st.tone}
                                    />
                                    <div className="uptray-card-meta">
                                        {active ? (
                                            <>
                                                <span className={`uptray-dot uptray-dot--${st.tone}`} />
                                                <span>{speedText}</span>
                                                <span className="uptray-card-eta">{fmtEta(s.etaMs)} left</span>
                                            </>
                                        ) : (
                                            <span className={`uptray-status uptray-status--${st.tone}`}>{st.text}</span>
                                        )}
                                    </div>
                                    {active && s.samples.length > 2 && (
                                        <Sparkline
                                            data={s.samples}
                                            variant={st.tone === "muted" ? "accent" : st.tone}
                                            height={22}
                                            className="uptray-card-spark"
                                            ariaLabel="Throughput"
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {detailsId &&
                createPortal(
                    <UploadDetailsModal sessionId={detailsId} onClose={() => setDetailsId(null)} />,
                    document.body
                )}
        </>
    );
};

export default UploadTray;
